import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Filter } from '../../models/filter-models';
import { TieringMode, TIERING_MODE_LABELS, TIERING_MODE_OPTIONS } from '../../models/enums';
import { BaseComponent } from '../../common/base.component';
import { CurrencyTierService } from '../../services/tiering/currency-tier.service';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { ClusterJewelTierService } from '../../services/tiering/cluster-jewel-tier.service';
import { FilterService } from '../../services/filter.service';
import { PresetsService } from '../../services/presets.service';
import { TieringCutoffHint } from '../../models/tiering-cutoff-hint.models';
import { buildTieringCutoffHint } from '../../services/tiering/tiering-cutoff-hint.util';
import { TieringCutoffHintComponent } from '../../common/tiering-cutoff-hint/tiering-cutoff-hint.component';
import { CustomCurrencyTieringModeDialogComponent } from '../../custom-currency-tiering-mode-dialog/custom-currency-tiering-mode-dialog.component';
import { CustomDivCardTieringModeDialogComponent } from '../../custom-div-card-tiering-mode-dialog/custom-div-card-tiering-mode-dialog.component';
import { CustomUniquesTieringModeDialogComponent } from '../../custom-uniques-tiering-mode-dialog/custom-uniques-tiering-mode-dialog.component';
import { CustomClusterJewelTieringModeDialogComponent } from '../../custom-cluster-jewel-tiering-mode-dialog/custom-cluster-jewel-tiering-mode-dialog.component';
import { poe1 } from '../../../game';

type DomainKey = 'currency' | 'divCards' | 'uniques' | 'clusterJewel';

interface DomainSlider {
  key: DomainKey;
  label: string;
  mode: TieringMode;
  index: number;
  modeLabel: string;
  isCustom: boolean;
  cutoffHint: TieringCutoffHint | null;
}

@Component({
  selector: 'app-progress-tiering-modes',
  imports: [
    TieringCutoffHintComponent,
    CustomCurrencyTieringModeDialogComponent,
    CustomDivCardTieringModeDialogComponent,
    CustomUniquesTieringModeDialogComponent,
    CustomClusterJewelTieringModeDialogComponent,
  ],
  templateUrl: './progress-tiering-modes.component.html',
  styleUrls: ['./progress-tiering-modes.component.scss'],
})
export class ProgressTieringModesComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input() refreshKey = 0;
  @Output() modesChanged = new EventEmitter<void>();

  masterIndex = 1;
  masterLabel = TIERING_MODE_LABELS.Normal;
  showMasterSlider = false;

  domains: DomainSlider[] = [];

  customDialogDomain: DomainKey | null = null;

  constructor(
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
    private clusterJewelTierService: ClusterJewelTierService,
    private filterService: FilterService,
    private presetsService: PresetsService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['refreshKey']) {
      this.syncFromFilter();
    }
  }

  onMasterSliderChange(event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    const mode = TIERING_MODE_OPTIONS[index];
    this.applyModeToAll(mode);
  }

  onDomainSliderChange(domain: DomainSlider, event: Event) {
    const index = Number((event.target as HTMLInputElement).value);
    const mode = TIERING_MODE_OPTIONS[index];
    this.applyMode(domain.key, mode);
  }

  openCustomDialog(domain: DomainSlider) {
    if (!domain.isCustom) return;
    this.customDialogDomain = domain.key;
  }

  closeCustomDialog() {
    this.customDialogDomain = null;
    this.syncFromFilter();
    this.filterService.updateFilter();
    this.modesChanged.emit();
  }

  private applyModeToAll(mode: TieringMode) {
    this.currencyTierService.setTieringMode(mode, this.filter);
    if (poe1 && !this.filter.ruthless) {
      this.currencyTierService.setDivCardTieringMode(mode, this.filter);
    }
    this.uniquesTierService.setTieringMode(mode, this.filter);
    if (poe1) {
      this.clusterJewelTierService.setTieringMode(mode, this.filter);
    }
    this.afterModeChange();
  }

  private applyMode(key: DomainKey, mode: TieringMode) {
    if (key === 'currency') {
      this.currencyTierService.setTieringMode(mode, this.filter);
    } else if (key === 'divCards') {
      this.currencyTierService.setDivCardTieringMode(mode, this.filter);
    } else if (key === 'uniques') {
      this.uniquesTierService.setTieringMode(mode, this.filter);
    } else {
      this.clusterJewelTierService.setTieringMode(mode, this.filter);
    }
    this.afterModeChange();
  }

  private afterModeChange() {
    this.syncFromFilter();
    this.filterService.updateFilter();
    this.modesChanged.emit();
  }

  private syncFromFilter() {
    const active = this.presetsService.getActiveTieringModeDomains(this.filter);
    this.domains = active.map(d => {
      const index = TIERING_MODE_OPTIONS.indexOf(d.mode);
      return {
        key: d.key,
        label: d.label,
        mode: d.mode,
        index: index >= 0 ? index : TIERING_MODE_OPTIONS.indexOf('Normal'),
        modeLabel: TIERING_MODE_LABELS[d.mode] ?? TIERING_MODE_LABELS.Normal,
        isCustom: d.mode === 'Custom',
        cutoffHint: this.buildCutoffHint(d.key, d.mode),
      };
    });

    const allSame = this.domains.length > 0 && this.domains.every(d => d.mode === this.domains[0].mode);
    this.showMasterSlider = this.domains.length > 2;
    if (allSame) {
      this.masterIndex = this.domains[0].index;
      this.masterLabel = this.domains[0].modeLabel;
    } else {
      // Leave the master thumb where it is; only the label reflects Mixed.
      this.masterLabel = 'Mixed';
    }
  }

  private buildCutoffHint(key: DomainKey, mode: TieringMode): TieringCutoffHint | null {
    if (key === 'clusterJewel') return null;
    const subject = key === 'divCards' ? 'divCards' : key;
    return buildTieringCutoffHint(subject, mode, this.filter, this.currencyTierService, this.uniquesTierService);
  }
}
